import { z } from 'zod';
import type {
  AssertionJson,
  AttestationJson,
  CreationOptionsJson,
  RequestOptionsJson,
} from '@/modules/webauthn';

// Client for sso-backend's cookie-authenticated JSON API. The SPA and the
// backend share one origin: every call goes to `/api/<backend path>` (the
// ingress — or the Vite dev proxy — strips `/api`), the browser attaches the
// host-only `sso_session` cookie by itself, and the backend's CSRF guard
// passes because the request is same-origin. Bodies are parsed fail-closed
// with per-endpoint zod schemas that declare only the fields consumers read.

// The backend answers every failure with `{ statusCode, message }` where
// message is a string or, for validation failures, a list of strings.
export class SsoApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'SsoApiError';
    this.status = status;
  }
}

// `authuser` is the backend's per-tab selector for the active account when
// several are signed into the browser; the session guard reads it from the
// query string, so it rides along on every cookie-authenticated call.
type SsoApiOptions = {
  authuser?: number | undefined;
};

type Query = Record<string, string | number | undefined>;

type RequestInput = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  authuser?: number | undefined;
  headers?: Record<string, string>;
};

// Absolute on purpose: Node's fetch (the vitest runtime) rejects relative
// URLs, and the page origin is the backend origin by construction.
function apiUrl(path: string, query: Query): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  const base = `${window.location.origin}/api${path}`;
  return qs === '' ? base : `${base}?${qs}`;
}

const ErrorBodySchema = z.looseObject({
  message: z.union([z.string(), z.array(z.string())]),
});

async function toError(res: Response): Promise<SsoApiError> {
  // Stryker disable next-line ArrowFunction: equivalent — null and undefined both fail the message-object safeParse
  const body: unknown = await res.json().catch(() => null);
  const parsed = ErrorBodySchema.safeParse(body);
  if (!parsed.success) {
    return new SsoApiError(res.status, `Request failed (HTTP ${res.status})`);
  }
  const { message } = parsed.data;
  return new SsoApiError(
    res.status,
    Array.isArray(message) ? message.join(', ') : message,
  );
}

function buildInit(
  method: RequestInput['method'],
  body: unknown,
  headers: Record<string, string> = {},
): RequestInit {
  const init: RequestInit = { method: method ?? 'GET' };
  const merged = { ...headers };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    merged['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (Object.keys(merged).length > 0) init.headers = merged;
  return init;
}

async function request(
  path: string,
  { method, query = {}, body, authuser, headers }: RequestInput = {},
): Promise<Response> {
  const res = await fetch(
    apiUrl(path, { ...query, authuser }),
    buildInit(method, body, headers),
  );
  if (!res.ok) throw await toError(res);
  return res;
}

async function requestJson<S extends z.ZodType>(
  schema: S,
  path: string,
  input?: RequestInput,
): Promise<z.output<S>> {
  const res = await request(path, input);
  const body: unknown = await res.json();
  return schema.parse(body);
}

// A path is safe only when the browser would resolve it on THIS origin, an
// absolute URL only when its origin is in the backend's redirect allowlist
// (getSsoConfig). Resolving through the URL parser instead of string-testing
// closes the `/\\evil.example` class: the WHATWG parser reads a backslash as a
// slash, so a leading-slash string can still land off-origin. The backend
// resolves the same way against its issuer origin.
export function isSafeReturnTo(
  url: string | null | undefined,
  allowedOrigins: readonly string[],
): url is string {
  if (url === null || url === undefined) return false;
  const here = window.location.origin;
  if (url.startsWith('/')) {
    return URL.canParse(url, here) && new URL(url, here).origin === here;
  }
  if (!URL.canParse(url)) return false;
  const origin = new URL(url).origin;
  return allowedOrigins.some(
    (allowed) => URL.canParse(allowed) && new URL(allowed).origin === origin,
  );
}

const SsoConfigSchema = z.looseObject({
  allowedRedirectOrigins: z.array(z.string()),
  googleEnabled: z.boolean(),
});
type SsoConfig = z.output<typeof SsoConfigSchema>;

export function getSsoConfig(): Promise<SsoConfig> {
  return requestJson(SsoConfigSchema, '/auth/sso/config');
}

const ClientInfoSchema = z.looseObject({ name: z.string() });

export async function getClientInfo(clientId: string): Promise<string> {
  const { name } = await requestJson(ClientInfoSchema, '/oauth/client-info', {
    query: { client_id: clientId },
  });
  return name;
}

const ConsentInfoSchema = z.looseObject({
  clientName: z.string(),
  scopes: z.array(
    z.looseObject({ scope: z.string(), description: z.string() }),
  ),
});
type ConsentInfo = z.output<typeof ConsentInfoSchema>;

export function getConsentInfo(
  authorizeQuery: Record<string, string>,
): Promise<ConsentInfo> {
  return requestJson(ConsentInfoSchema, '/oauth/consent-info', {
    query: authorizeQuery,
  });
}

const EmailSchema = z.looseObject({ email: z.string() });

export async function checkMagicLink(token: string): Promise<string> {
  const { email } = await requestJson(
    EmailSchema,
    '/auth/email/magic-link/check',
    { query: { token } },
  );
  return email;
}

const AvailableSchema = z.looseObject({ available: z.boolean() });

export async function checkUsername(
  username: string,
  opts: SsoApiOptions = {},
): Promise<boolean> {
  const { available } = await requestJson(
    AvailableSchema,
    '/users/me/check-username',
    { query: { username }, authuser: opts.authuser },
  );
  return available;
}

export async function requestMagicLink(
  email: string,
  returnTo: string | null,
): Promise<void> {
  await request('/auth/email/magic-link/request', {
    method: 'POST',
    query: { returnTo: returnTo ?? undefined },
    body: { email },
  });
}

const RedirectToSchema = z.looseObject({ redirectTo: z.string() });

export async function verifyMagicLink(
  token: string,
  returnTo: string | null,
): Promise<string> {
  const { redirectTo } = await requestJson(
    RedirectToSchema,
    '/auth/email/magic-link/verify',
    {
      method: 'POST',
      body: { token, ...(returnTo !== null && { returnTo }) },
    },
  );
  return redirectTo;
}

export async function verifyMagicLinkCode(input: {
  email: string;
  code: string;
  returnTo: string | null;
}): Promise<string> {
  const { redirectTo } = await requestJson(
    RedirectToSchema,
    '/auth/email/magic-link/verify-code',
    {
      method: 'POST',
      body: {
        email: input.email,
        code: input.code,
        ...(input.returnTo !== null && { returnTo: input.returnTo }),
      },
    },
  );
  return redirectTo;
}

const RequestOptionsSchema = z.looseObject({
  challenge: z.string(),
  allowCredentials: z.array(z.looseObject({ id: z.string() })).optional(),
});

export function passkeyLoginOptions(): Promise<RequestOptionsJson> {
  return requestJson(RequestOptionsSchema, '/auth/passkey/login/options', {
    method: 'POST',
    body: {},
  });
}

// The sign-in verifiers answer `{ redirectTo }`: where the browser goes next
// (the caller's returnTo when the backend accepts it, the dashboard, profile
// setup for a brand-new account, or the sign-in form with an error).
export async function passkeyLoginVerify(
  assertion: AssertionJson,
  returnTo: string | null,
): Promise<string> {
  const { redirectTo } = await requestJson(
    RedirectToSchema,
    '/auth/passkey/login/verify',
    {
      method: 'POST',
      body: { ...assertion, ...(returnTo !== null && { returnTo }) },
    },
  );
  return redirectTo;
}

const MessageSchema = z.looseObject({ message: z.string() });

export async function getWalletNonce(address: string): Promise<string> {
  const { message } = await requestJson(MessageSchema, '/auth/wallet/nonce', {
    query: { address },
  });
  return message;
}

export async function walletVerify(input: {
  address: string;
  signature: string;
  returnTo: string | null;
}): Promise<string> {
  const { redirectTo } = await requestJson(
    RedirectToSchema,
    '/auth/wallet/verify',
    {
      method: 'POST',
      body: {
        address: input.address,
        signature: input.signature,
        ...(input.returnTo !== null && { returnTo: input.returnTo }),
      },
    },
  );
  return redirectTo;
}

const MeSchema = z.looseObject({
  username: z.string().nullable(),
  email: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  setupRequired: z.boolean(),
  createdAt: z.string(),
});
export type Me = z.output<typeof MeSchema>;

export function getMe(opts: SsoApiOptions = {}): Promise<Me> {
  return requestJson(MeSchema, '/users/me', { authuser: opts.authuser });
}

const AccountsSchema = z.looseObject({
  accounts: z.array(
    z.looseObject({
      // null on sessions created before the multi-account migration — such an
      // account cannot be addressed with ?authuser and is not switchable.
      authuser: z.number().nullable(),
      displayName: z.string(),
      email: z.string().nullable(),
      username: z.string().nullable(),
      avatarUrl: z.string().nullable(),
      isDefault: z.boolean(),
    }),
  ),
});
export type Account = z.output<typeof AccountsSchema>['accounts'][number];

export async function listAccounts(
  opts: SsoApiOptions = {},
): Promise<Account[]> {
  const { accounts } = await requestJson(AccountsSchema, '/accounts', {
    authuser: opts.authuser,
  });
  return accounts;
}

// Makes one signed-in account the browser's default — the one every request
// without an explicit `authuser` resolves to. A deliberate POST on the
// backend (never a side effect of a read), so it is called only once a page
// has decided to act as that account. 404 when the index is not a live
// member of this browser session.
export async function setDefaultAccount(authuser: number): Promise<void> {
  await request('/accounts/default', { method: 'POST', body: { authuser } });
}

const DescriptorSchema = z.looseObject({
  username: z.string().nullable(),
  email: z.string().nullable(),
  wallet: z.string().nullable(),
});
const SessionInfoSchema = z.looseObject({
  current: DescriptorSchema,
  expected: z
    .looseObject({
      ...DescriptorSchema.shape,
      // The other user's index in this browser session when they are also
      // signed in here, null when they are not.
      authuser: z.number().nullable().optional(),
    })
    .nullable(),
});
type SessionInfo = z.output<typeof SessionInfoSchema>;

export function getSessionInfo(
  expectedSessionId: string | null,
  opts: SsoApiOptions = {},
): Promise<SessionInfo> {
  return requestJson(SessionInfoSchema, '/auth/session-info', {
    query: { session_id: expectedSessionId ?? undefined },
    authuser: opts.authuser,
  });
}

// The demo stand's mint: not cookie-authenticated — the static DEMO_API_KEY
// typed into the operator page is the credential — and the backend signs
// THIS browser out first, so the cookie it sets carries the new account
// alone. 404 when the deployment has no DEMO_API_KEY at all.
export async function demoLogin(input: {
  apiKey: string;
  clientId: string;
}): Promise<void> {
  await request('/demo/login', {
    method: 'POST',
    body: { client_id: input.clientId },
    headers: { Authorization: `Bearer ${input.apiKey}` },
  });
}

export async function logout(input: {
  authuser?: number | undefined;
  everywhere?: boolean;
}): Promise<void> {
  await request('/auth/logout', {
    method: 'POST',
    query: { everywhere: input.everywhere === true ? 'true' : undefined },
    authuser: input.authuser,
  });
}

const AccountLinksSchema = z.array(
  z.looseObject({ provider: z.string(), providerAccountId: z.string() }),
);
export type AccountLink = z.output<typeof AccountLinksSchema>[number];

export function getAccountLinks(
  opts: SsoApiOptions = {},
): Promise<AccountLink[]> {
  return requestJson(AccountLinksSchema, '/auth/account/links', {
    authuser: opts.authuser,
  });
}

const PasskeysSchema = z.array(
  z.looseObject({
    id: z.string(),
    displayName: z.string().nullable(),
    deviceType: z.string(),
    backedUp: z.boolean(),
  }),
);
export type Passkey = z.output<typeof PasskeysSchema>[number];

export function listPasskeys(opts: SsoApiOptions = {}): Promise<Passkey[]> {
  return requestJson(PasskeysSchema, '/auth/passkey/credentials', {
    authuser: opts.authuser,
  });
}

const SessionsSchema = z.looseObject({
  sessions: z.array(
    z.looseObject({
      id: z.string(),
      createdAt: z.string(),
      current: z.boolean(),
    }),
  ),
});
export type DeviceSession = z.output<typeof SessionsSchema>['sessions'][number];

export async function listSessions(
  opts: SsoApiOptions = {},
): Promise<DeviceSession[]> {
  const { sessions } = await requestJson(SessionsSchema, '/auth/sessions', {
    authuser: opts.authuser,
  });
  return sessions;
}

const ConnectedAppsSchema = z.looseObject({
  apps: z.array(z.looseObject({ clientId: z.string(), name: z.string() })),
});
type ConnectedApp = z.output<typeof ConnectedAppsSchema>['apps'][number];

export async function listConnectedApps(
  opts: SsoApiOptions = {},
): Promise<ConnectedApp[]> {
  const { apps } = await requestJson(
    ConnectedAppsSchema,
    '/users/me/connected-apps',
    { authuser: opts.authuser },
  );
  return apps;
}

const UnlinkResultSchema = z.looseObject({
  // The user's other sessions that had signed in with the removed method,
  // ended together with their refresh tokens on every unlink; the session
  // making the call is never counted or ended.
  sessionsRevoked: z.number(),
  // Whether every other session was ended too — the echo of
  // `revokeOtherSessions`, the only way to reach sessions the backend cannot
  // attribute to a method (passkey logins, sessions predating attribution).
  otherSessionsRevoked: z.boolean(),
});
export type UnlinkResult = z.output<typeof UnlinkResultSchema>;

// `revokeOtherSessions` carries the user's own answer to "also sign out of
// your other devices?": the backend never ends sessions it cannot attribute
// to the removed method unless asked to.
export function unlinkProvider(
  provider: string,
  address: string | null,
  opts: SsoApiOptions & { revokeOtherSessions?: boolean } = {},
): Promise<UnlinkResult> {
  return requestJson(
    UnlinkResultSchema,
    `/auth/account/unlink/${encodeURIComponent(provider)}`,
    {
      method: 'DELETE',
      query: {
        address: address ?? undefined,
        revokeOtherSessions:
          opts.revokeOtherSessions === true ? 'true' : undefined,
      },
      authuser: opts.authuser,
    },
  );
}

export async function requestLinkEmail(
  email: string,
  opts: SsoApiOptions = {},
): Promise<void> {
  await request('/auth/account/link/email/request', {
    method: 'POST',
    body: { email },
    authuser: opts.authuser,
  });
}

export async function verifyLinkEmail(
  email: string,
  code: string,
  opts: SsoApiOptions = {},
): Promise<void> {
  await request('/auth/account/link/email/verify', {
    method: 'POST',
    body: { email, code },
    authuser: opts.authuser,
  });
}

export async function getLinkWalletNonce(
  address: string,
  opts: SsoApiOptions = {},
): Promise<string> {
  const { message } = await requestJson(
    MessageSchema,
    '/auth/account/link/wallet/nonce',
    { query: { address }, authuser: opts.authuser },
  );
  return message;
}

export async function linkWallet(
  address: string,
  signature: string,
  opts: SsoApiOptions = {},
): Promise<void> {
  await request('/auth/account/link/wallet', {
    method: 'POST',
    body: { address, signature },
    authuser: opts.authuser,
  });
}

const CreationOptionsSchema = z.looseObject({
  challenge: z.string(),
  rp: z.looseObject({ name: z.string() }),
  user: z.looseObject({
    id: z.string(),
    name: z.string(),
    displayName: z.string(),
  }),
  pubKeyCredParams: z.array(
    z.looseObject({ type: z.literal('public-key'), alg: z.number() }),
  ),
  excludeCredentials: z.array(z.looseObject({ id: z.string() })).optional(),
});

export function passkeyRegisterOptions(
  authenticatorAttachment: 'platform' | 'cross-platform',
  opts: SsoApiOptions = {},
): Promise<CreationOptionsJson> {
  return requestJson(CreationOptionsSchema, '/auth/passkey/register/options', {
    method: 'POST',
    body: { authenticatorAttachment },
    authuser: opts.authuser,
  });
}

export async function passkeyRegisterVerify(
  attestation: AttestationJson,
  opts: SsoApiOptions = {},
): Promise<void> {
  await request('/auth/passkey/register/verify', {
    method: 'POST',
    body: attestation,
    authuser: opts.authuser,
  });
}

export async function deletePasskey(
  id: string,
  opts: SsoApiOptions = {},
): Promise<void> {
  await request(`/auth/passkey/credentials/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    authuser: opts.authuser,
  });
}

export async function revokeSession(
  id: string,
  opts: SsoApiOptions = {},
): Promise<void> {
  await request(`/auth/sessions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    authuser: opts.authuser,
  });
}

export async function updateProfile(
  input: { username?: string; avatarUrl?: string },
  opts: SsoApiOptions = {},
): Promise<void> {
  await request('/users/me/profile', {
    method: 'PATCH',
    body: input,
    authuser: opts.authuser,
  });
}

const AvatarUrlSchema = z.looseObject({ avatarUrl: z.string() });

export async function uploadAvatar(
  file: File,
  opts: SsoApiOptions = {},
): Promise<string> {
  const form = new FormData();
  form.append('avatar', file);
  const { avatarUrl } = await requestJson(AvatarUrlSchema, '/users/me/avatar', {
    method: 'POST',
    body: form,
    authuser: opts.authuser,
  });
  return avatarUrl;
}

export async function deleteAvatar(opts: SsoApiOptions = {}): Promise<void> {
  await request('/users/me/avatar', {
    method: 'DELETE',
    authuser: opts.authuser,
  });
}
